import type { ReactElement } from "react";
import { Monitor, Moon, Sun } from "lucide-react";
import { useThemePreference, type ThemePreference } from "@tokens/index";
import { SegmentedControl, type SegmentedControlOption } from "@primitives/SegmentedControl";

/**
 * Theme preference control: a labeled `SegmentedControl` bound to the theme
 * store. System · Light · Dark, each with its glyph. Lives on the Appearance
 * settings page; name it with `labelledBy` from the row label.
 */

const options: readonly SegmentedControlOption<ThemePreference>[] = [
    { value: "system", label: "System", icon: Monitor },
    { value: "light", label: "Light", icon: Sun },
    { value: "dark", label: "Dark", icon: Moon },
];

export interface ThemePreferenceToggleProps {
    /** Id of the visible label. Falls back to a plain-text group name. */
    readonly labelledBy?: string;
    /** Id of the text that explains the control. */
    readonly describedBy?: string;
    readonly fullWidth?: boolean;
}

export function ThemePreferenceToggle({
    labelledBy,
    describedBy,
    fullWidth = false,
}: ThemePreferenceToggleProps): ReactElement {
    const { preference, setPreference } = useThemePreference();

    return (
        <SegmentedControl
            options={options}
            value={preference}
            onChange={setPreference}
            tone="neutral"
            fullWidth={fullWidth}
            {...(labelledBy !== undefined ? { labelledBy } : { ariaLabel: "Theme preference" })}
            {...(describedBy !== undefined ? { "aria-describedby": describedBy } : {})}
        />
    );
}
